import React, { useCallback, useState } from "react";
import PopoverButton from "@dtci/spellbook/PopoverButton";
import MenuList from "@dtci/spellbook/MenuList";
import {
  revalidatePromotionIssues,
  resyncPromotionEnrichment,
} from "ppt/api";
import { usePPTToasts } from "../../hooks/usePPTToasts";

const ACTION_REVALIDATE = "revalidate";
const ACTION_RESYNC = "resync";

const OPTIONS = [
  {
    value: ACTION_REVALIDATE,
    label: "Re-validate Issues",
  },
  {
    value: ACTION_RESYNC,
    label: "Re-sync from Rightsline",
  },
];

/**
 * Kebab menu attached to the promotion detail header.
 *
 * Two recovery actions, both safe to expose to anyone with promotion-read
 * permission:
 *   - Re-validate Issues — re-runs the assembly issue evaluator over
 *     existing data. Idempotent. Returns the refreshed PromotionDetail
 *     so the parent can update its store.
 *   - Re-sync from Rightsline — re-enqueues every linked TDRO for refetch
 *     by the scheduled worker. Async; returns only the requeued count.
 *     Parent doesn't need to refresh immediately, but a delayed reload
 *     (~30s) lets users see the updated client identities once the worker
 *     drains.
 *
 * Disables the menu while a request is in flight to prevent double-submits.
 */
export const PPTPromotionActionsMenu = ({ promotionId, onActionComplete }) => {
  const { showSuccessToast, showErrorToast } = usePPTToasts();
  const [busy, setBusy] = useState(false);

  const handleRevalidate = useCallback(async () => {
    setBusy(true);
    try {
      const response = await revalidatePromotionIssues(promotionId);
      const issueCount = response?.data?.issueCount;
      const message =
        typeof issueCount === "number"
          ? `Re-validated issues — ${issueCount} open after pass.`
          : "Re-validated issues.";
      showSuccessToast(message);
      if (onActionComplete) onActionComplete({ action: ACTION_REVALIDATE });
    } catch (err) {
      console.error("Re-validate Issues failed:", err);
      showErrorToast("Re-validate Issues failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [promotionId, onActionComplete, showSuccessToast, showErrorToast]);

  const handleResync = useCallback(async () => {
    setBusy(true);
    try {
      const { requeuedCount } = await resyncPromotionEnrichment(promotionId);
      const message =
        requeuedCount > 0
          ? `Re-syncing ${requeuedCount} TDRO${requeuedCount === 1 ? "" : "s"} from Rightsline. Refresh the page in a moment to see updates.`
          : "No TDROs linked to this promotion yet — nothing to re-sync.";
      showSuccessToast(message);
      if (onActionComplete) onActionComplete({ action: ACTION_RESYNC, requeuedCount });
    } catch (err) {
      console.error("Re-sync from Rightsline failed:", err);
      showErrorToast("Re-sync from Rightsline failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [promotionId, onActionComplete, showSuccessToast, showErrorToast]);

  const handleSelect = useCallback(
    (option) => {
      if (busy) return;
      switch (option?.value) {
        case ACTION_REVALIDATE:
          handleRevalidate();
          break;
        case ACTION_RESYNC:
          handleResync();
          break;
        default:
          break;
      }
    },
    [busy, handleRevalidate, handleResync],
  );

  return (
    <PopoverButton
      isDisabled={busy}
      buttonProps={{ styleType: "link", colorType: "base" }}
      testMetadata={{ button: { "data-testid": "ppt-promotion-actions-kebab" } }}
    >
      <MenuList
        options={OPTIONS}
        onChange={handleSelect}
        isStandalone
      />
    </PopoverButton>
  );
};

export default PPTPromotionActionsMenu;
